// Iter92 / Phase 4D — Shared WES context provenance strip rendered
// by each of the five auto-scan panels near the snapshot summary.
//
// Visual / payload provenance only. Never ranks. Never recommends.
// Never blocks the scan run. The presence of WES context is purely
// informational — the existing evaluator gates decide whether a
// scan returns NO_TRADE / BLOCKED.
//
// HARD CONSTRAINTS:
//  * Pure presentational React component. No HTTP. No persistence.
//  * Reads only the summarized WES context from `useWESContext`
//    (via the panel that owns this strip's render lifecycle).

import React from "react";
import { Activity, AlertTriangle } from "lucide-react";

export default function WesProvenanceStrip({ moduleId, wesSummary }) {
  if (!wesSummary || !wesSummary.available) {
    return (
      <div
        data-testid={`${moduleId}-auto-scan-wes-strip`}
        className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[10px] font-mono flex items-center gap-3 flex-wrap text-[#FFB800]"
      >
        <AlertTriangle size={12} />
        <span data-testid={`${moduleId}-auto-scan-wes-strip-missing`}>
          No WES context loaded — scan will run structurally
          incomplete.
        </span>
      </div>
    );
  }
  const {
    fingerprintShort, fingerprint, ageSeconds, stale,
    availableSymbols,
  } = wesSummary;
  return (
    <div
      data-testid={`${moduleId}-auto-scan-wes-strip`}
      className="px-4 py-2 border-b border-[#26262B] bg-[#00FF88]/5 text-[10px] font-mono flex items-center gap-3 flex-wrap text-[#00FF88]"
    >
      <Activity size={12} />
      <span data-testid={`${moduleId}-auto-scan-wes-strip-available`}>
        Using WES context {fingerprintShort || "—"} · fresh
        {ageSeconds != null ? ` ${ageSeconds}s ago` : " —"}
      </span>
      <span className="text-[#00FF88]/40">·</span>
      <span
        data-testid={`${moduleId}-auto-scan-wes-strip-symbols-count`}
        className="text-[#00FF88]/80"
      >
        {availableSymbols.length} sym(s)
      </span>
      {fingerprintShort ? (
        <span
          data-testid={`${moduleId}-auto-scan-wes-strip-fingerprint`}
          title={fingerprint || ""}
          className="px-1.5 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88] uppercase tracking-[0.12em]"
        >
          {fingerprintShort}
        </span>
      ) : (
        <span
          data-testid={`${moduleId}-auto-scan-wes-strip-fingerprint-missing`}
          className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
        >
          WES engine fingerprint unavailable.
        </span>
      )}
      {stale && (
        <span
          data-testid={`${moduleId}-auto-scan-wes-strip-stale`}
          className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em] inline-flex items-center gap-1"
        >
          <AlertTriangle size={9} />
          WES context stale — rebuild before relying on this scan.
        </span>
      )}
    </div>
  );
}
